import { useLayoutEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Plus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { TemplateMenu } from "@/components/Template/RxTemplates";

// General signs, kept with the vitals (as before) so history and trends read them unchanged
const SIGNS = [
  { field: "pallor",  label: "Pallor"  },
  { field: "icterus", label: "Icterus" },
  { field: "edema",   label: "Edema"   },
];
const SIGN_OPTIONS = ["None", "Mild", "Moderate", "Severe"];

// Systems a doctor writes findings under; each button starts (or returns to) that system's line
const SYSTEMS = ["CVS", "RS", "P/A", "CNS", "Local"];

/** On-examination findings: general signs as chips, then the findings written line by line ("CVS: S1 S2 normal") */
export default function ExaminationSection() {
    const { register, setValue, getValues, watch, formState } = useFormContext();
    const boxRef = useRef<HTMLDivElement>(null);
    const vitals = watch("vitals") || {};
    const findings = watch("examination");

    // The box grows with the findings via field-sizing, which only Chrome and Edge have; Safari and Firefox grow it here
    useLayoutEffect(() => {
        if (CSS.supports?.("field-sizing", "content")) return;
        const el = boxRef.current?.querySelector("textarea");
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    }, [findings]);

    const addSystem = (system: string) => {
        const text: string = getValues("examination") || "";
        const prefix = `${system}: `;
        const line = text.split("\n").find(l => l.trim().toLowerCase().startsWith(prefix.trim().toLowerCase()));
        let next = text;
        let caret: number;
        if (line !== undefined) {
            // Already written: go to the end of that line
            caret = text.indexOf(line) + line.length;
        } else {
            next = text.trim() ? `${text.replace(/\s+$/, "")}\n${prefix}` : prefix;
            caret = next.length;
            setValue("examination", next, { shouldDirty: true });
        }
        requestAnimationFrame(() => {
            const el = boxRef.current?.querySelector("textarea");
            if (!el) return;
            el.focus();
            el.setSelectionRange(caret, caret);
        });
    };

    return (
        <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
                {SIGNS.map(({ field: sign, label }) => {
                    const value = String(vitals[sign] || "").trim();
                    return (
                        <DropdownMenu key={sign}>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className={cn(
                                        "inline-flex items-center gap-1 h-7 touch:h-9 px-2 rounded text-[13px] transition-colors",
                                        value
                                            ? "bg-muted text-foreground hover:bg-accent"
                                            : "border border-dashed border-field text-muted-foreground hover:text-foreground hover:bg-accent",
                                    )}
                                >
                                    {value ? <>{label}: <span className="font-medium">{value}</span></> : <><Plus className="w-3 h-3" />{label}</>}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="min-w-[140px]">
                                {SIGN_OPTIONS.map(opt => (
                                    <DropdownMenuItem key={opt} onSelect={() => setValue(`vitals.${sign}`, opt, { shouldDirty: true })} className={cn(value === opt && "font-semibold")}>
                                        {opt}
                                    </DropdownMenuItem>
                                ))}
                                {value && (
                                    <DropdownMenuItem onSelect={() => setValue(`vitals.${sign}`, "", { shouldDirty: true })} className="text-muted-foreground">
                                        Not examined
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                })}
                <span className="mx-1 h-4 w-px bg-border" aria-hidden />
                {SYSTEMS.map(system => (
                    <button
                        key={system}
                        type="button"
                        onClick={() => addSystem(system)}
                        title={`Write ${system} findings`}
                        className="inline-flex items-center h-7 touch:h-9 px-2 rounded text-[13px] text-primary hover:bg-accent transition-colors"
                    >
                        {system}
                    </button>
                ))}
            </div>
            <div ref={boxRef} className="relative">
                <Textarea
                    {...register("examination")}
                    placeholder="Findings on examination…"
                    rows={2}
                    className={cn(
                        "text-sm bg-card border border-field rounded-md",
                        "focus:border-ring focus:outline-none focus-visible:ring-1 focus-visible:ring-ring/20",
                        "placeholder:text-muted-foreground/60 placeholder:font-normal",
                        // Grows with the findings, as a written O/E block does
                        "resize-none [field-sizing:content] min-h-[60px] max-h-72 pl-2.5 pr-9 py-2",
                        formState.errors?.examination && "border-destructive/60"
                    )}
                    maxLength={2000}
                />
                <TemplateMenu type="examination" className="absolute right-1 top-1" />
            </div>
            {formState.errors?.examination && (
                <div className="text-xs text-destructive mt-1">
                    {formState.errors.examination.message as string}
                </div>
            )}
        </div>
    );
}
